import SVGIcon from 'components/SVGIcon/SVGIcon';
import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import money from '../../utils/formatMoney';
import media from '../../utils/mediaStyle';
import utilsMessages from '../../utils/messages';
import FreeShippingIcon from './FreeShippingIcon';
import canOrderIcon from './canOrderIcon.svg';

const DeliveryFeeContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  height: 100%;
  background-color: #ffffff;
  padding: 0px 10px;

  ${media.md`
    background-color: transparent;
  `}
`;

const DeliveryFeeTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #343538;
`;

const DeliveryProgressWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const DeliveryProgressContainer = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.12);

  ${media.md`
    width: 320px;
  `}
`;
const MoqFlag = styled.div`
  display: ${({ noDisplay }) => (noDisplay ? 'none' : 'block')};
  position: absolute;
  left: ${({ left }) => left};
  top: 0;
  width: 1px;
  height: 6px;
  background: #f38a3e;
`;
const DeliveryProgressValue = styled.div`
  position: absolute;
  z-index: 1;
  left: 0;
  top: 0;
  width: ${({ width }) => width};
  height: 6px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  background: ${({ color }) => color};
  transition: width 0.4s;
`;
const DeliveryProgressPoint = styled.div`
  position: absolute;
  z-index: 2;
  left: ${({ left }) => left};
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
  border: 3px solid ${({ borderColor }) => borderColor};
  top: -3px;
  transition: left 0.4s;
`;
interface IDeliveryFeeSection {
  deliveryFee: any;
  subTotal: number;
  cartMoq: number;
}

const DeliveryFeeSection = (props: IDeliveryFeeSection) => {
  const { deliveryFee, subTotal, cartMoq = 0 } = props;
  const deliveryFeeProgressRef = useRef();

  const isLessThanCartMOQ = useMemo(() => (!!subTotal ? subTotal < cartMoq : true), [cartMoq, subTotal]);

  const deliveryFeeStyles = useMemo(() => {
    if (!deliveryFeeProgressRef?.current || !subTotal || !deliveryFee?.fee) {
      return {
        width: 0,
        progressBackground: '#F38A3E',
        backgroundColor: 'rgba(255, 247, 214, 0.40)',
      };
    }
    const progressWidth = (deliveryFeeProgressRef?.current as any)?.offsetWidth;

    const destiationMoq =
      (!deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0) || cartMoq > deliveryFee?.deliveryFeeThreshold
        ? cartMoq
        : deliveryFee?.deliveryFeeThreshold;

    if (!destiationMoq) {
      return {
        width: progressWidth,
        progressBackground: '#43A047',
        backgroundColor: 'rgba(239, 255, 214, 0.40)',
      };
    }

    const width = (subTotal / destiationMoq) * progressWidth;

    if (isLessThanCartMOQ) {
      return {
        width: width,
        progressBackground: '#F38A3E',
        backgroundColor: 'rgba(255, 247, 214, 0.40)',
      };
    }

    if (deliveryFee?.isFreeDelivery || deliveryFee?.remainingToFree <= 0) {
      return {
        width: progressWidth,
        progressBackground: '#43A047',
        backgroundColor: 'rgba(239, 255, 214, 0.40)',
      };
    }

    return {
      width: Math.min(width, progressWidth),
      progressBackground: '#43A047',
      backgroundColor: 'rgba(239, 255, 214, 0.40)',
    };
  }, [subTotal, deliveryFeeProgressRef?.current, isLessThanCartMOQ, deliveryFee, cartMoq]);

  const deliveryFeeTextComponent = useCallback(() => {
    return (
      <DeliveryFeeTitle>
        {isLessThanCartMOQ ? (
          <>
            <FormattedMessage {...utilsMessages.buyMore} />
            {` `}
            <span style={{ fontWeight: 600 }}>{money(cartMoq - subTotal)}đ</span>
            {` `}
            <FormattedMessage {...utilsMessages.canOrder} />
          </>
        ) : !deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0 ? (
          <>
            <FormattedMessage {...utilsMessages.youCanPlaceAnOrder} />
          </>
        ) : deliveryFee?.isFreeDelivery ? (
          <>
            <FormattedMessage {...utilsMessages.freeShippingTitle} />
            {` `}
            <span style={{ fontWeight: 600 }}>
              <FormattedMessage {...utilsMessages.freeShippingContent} />
            </span>
          </>
        ) : (
          <>
            <FormattedMessage {...utilsMessages.buyMore} />
            {` `}
            <span style={{ fontWeight: 600 }}>{money(deliveryFee?.remainingToFree)}đ</span>
            {` `}
            <FormattedMessage {...utilsMessages.toGetFreeService} />
          </>
        )}
      </DeliveryFeeTitle>
    );
  }, [subTotal, deliveryFee, isLessThanCartMOQ, cartMoq]);

  return (
    <DeliveryFeeContainer bgColor={deliveryFeeStyles.backgroundColor}>
      {deliveryFeeTextComponent()}
      <DeliveryProgressWrapper>
        <DeliveryProgressContainer ref={deliveryFeeProgressRef}>
          <MoqFlag
            left={`${(cartMoq / deliveryFee?.deliveryFeeThreshold) * (deliveryFeeProgressRef?.current as any)?.offsetWidth}px`}
            noDisplay={
              !cartMoq ||
              cartMoq >= deliveryFee?.deliveryFeeThreshold ||
              (!deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0)
            }
          />
          <DeliveryProgressValue width={`${deliveryFeeStyles.width}px`} color={deliveryFeeStyles.progressBackground} />
          <DeliveryProgressPoint
            left={`${deliveryFeeStyles?.width ? deliveryFeeStyles?.width - 6 : 0}px`}
            borderColor={deliveryFeeStyles.progressBackground}
          />
        </DeliveryProgressContainer>
        {(!deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0) ||
        cartMoq >= deliveryFee?.deliveryFeeThreshold ? (
          <SVGIcon src={canOrderIcon} />
        ) : (
          <FreeShippingIcon />
        )}
      </DeliveryProgressWrapper>
    </DeliveryFeeContainer>
  );
};

export default DeliveryFeeSection;
